"use client";

import {
  VIEWER_DETAIL_IMAGE_MAX_HEIGHT_PERCENT,
  VIEWER_DETAIL_PADDING_X,
  VIEWER_DETAIL_PADDING_Y,
  VIEWER_DETAIL_PANEL_COLUMN_GAP,
  VIEWER_DETAIL_PANEL_PADDING,
  VIEWER_DETAIL_SECTION_GAP,
  VIEWER_DETAIL_TOC_SCROLL_MARGIN,
} from "@/config/layout";
import { VIEWER_SLIDE_CROSSFADE_MS } from "@/config/timing";
import { useViewerLanguage } from "@/lib/i18n";
import type { TOCItem } from "@/types/modules";
import type {
  SlideImage,
  ViewerSlideDetails,
  ViewerTitleCard,
} from "@/utils/viewer-slide-details";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  useEffect,
  useRef,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import ModuleTOC from "./modules/module-toc";

const SLIDE_ASPECT = 16 / 9;

const CELL_SCROLL_CLASS =
  "min-h-0 overflow-y-auto overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden";

const DETAIL_ELEMENTS = [
  "p",
  "strong",
  "em",
  "del",
  "code",
  "br",
  "ul",
  "ol",
  "li",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
];

function DetailHeading({ children }: { children?: ReactNode }) {
  return <p className="font-medium">{children}</p>;
}

const DETAIL_COMPONENTS: Components = {
  p: ({ children }) => <p>{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-4">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-4">{children}</ol>,
  code: ({ children }) => <span>{children}</span>,
  h1: DetailHeading,
  h2: DetailHeading,
  h3: DetailHeading,
  h4: DetailHeading,
  h5: DetailHeading,
  h6: DetailHeading,
};

function SlideDetailsText({ markdown }: { markdown: string }) {
  return (
    <div className="space-y-2 text-sm leading-relaxed opacity-90">
      <ReactMarkdown
        skipHtml
        allowedElements={DETAIL_ELEMENTS}
        unwrapDisallowed
        components={DETAIL_COMPONENTS}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
}

function useActiveEntryInView(
  cellRef: RefObject<HTMLElement | null>,
  activeSlideNumber: number | null,
  toc: Record<string, TOCItem> | null,
) {
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    const cell = cellRef.current;
    if (!cell) return;
    const item = cell.querySelector<HTMLElement>("[data-toc-active]");
    if (!item) return;
    const cellRect = cell.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    let delta = 0;
    if (itemRect.top < cellRect.top + VIEWER_DETAIL_TOC_SCROLL_MARGIN) {
      delta = itemRect.top - cellRect.top - VIEWER_DETAIL_TOC_SCROLL_MARGIN;
    } else if (
      itemRect.bottom >
      cellRect.bottom - VIEWER_DETAIL_TOC_SCROLL_MARGIN
    ) {
      delta =
        itemRect.bottom - cellRect.bottom + VIEWER_DETAIL_TOC_SCROLL_MARGIN;
    }
    if (delta === 0) return;
    cell.scrollTo({
      top: cell.scrollTop + delta,
      behavior: reduceMotion ? "auto" : "smooth",
    });
  }, [cellRef, activeSlideNumber, toc, reduceMotion]);
}

export function ViewerDetailColumn({
  details,
  slideId,
  slideTotal,
  image,
  titleCard,
  toc,
  availableSlideNumbers,
  onJumpToSlide,
  onOpenImage,
}: {
  details: ViewerSlideDetails | null;
  slideId: string;
  slideTotal: number;
  image: SlideImage | null;
  titleCard: ViewerTitleCard;
  toc: Record<string, TOCItem> | null;
  availableSlideNumbers: ReadonlySet<number>;
  onJumpToSlide: (slideNumber: number) => void;
  onOpenImage?: () => void;
}) {
  const reduceMotion = useReducedMotion();
  const { t } = useViewerLanguage();
  const tocCellRef = useRef<HTMLElement>(null);
  const fadeSeconds = reduceMotion ? 0 : VIEWER_SLIDE_CROSSFADE_MS / 1000;
  const slot = details?.paletteSlot ?? 1;
  const slideNumber = details?.slideNumber ?? null;
  useActiveEntryInView(tocCellRef, slideNumber, toc);

  const surface = {
    "--detail-bg": `var(--handyout-chart-${slot})`,
    "--detail-fg": `var(--handyout-chart-${slot}-fg)`,
    backgroundColor: "var(--detail-bg)",
    color: "var(--detail-fg)",
    paddingLeft: VIEWER_DETAIL_PADDING_X,
    paddingRight: VIEWER_DETAIL_PADDING_X,
    paddingTop: VIEWER_DETAIL_PADDING_Y,
    paddingBottom: VIEWER_DETAIL_PADDING_Y,
    gap: VIEWER_DETAIL_SECTION_GAP,
  } as CSSProperties;
  const imageWidth: CSSProperties = {
    width: `min(100%, ${VIEWER_DETAIL_IMAGE_MAX_HEIGHT_PERCENT * SLIDE_ASPECT}cqh)`,
  };
  const panel: CSSProperties = {
    paddingTop: VIEWER_DETAIL_PANEL_PADDING,
    columnGap: VIEWER_DETAIL_PANEL_COLUMN_GAP,
  };
  const frameKey = image ? `image:${slideId}` : "title-card";
  const slideLabel =
    slideNumber == null
      ? null
      : slideTotal > 0
        ? t("Slide {number} of {total}", {
            number: slideNumber,
            total: slideTotal,
          })
        : t("Slide {number}", { number: slideNumber });
  const detailText = details?.details ?? null;

  return (
    <div
      className="flex h-full w-full flex-col overflow-hidden transition-colors duration-500 [container-type:size]"
      style={surface}
    >
      <div
        className="relative aspect-video shrink-0 self-center overflow-hidden rounded-lg ring-1 ring-white/30"
        style={imageWidth}
      >
        <AnimatePresence initial={false}>
          <motion.div
            key={frameKey}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: fadeSeconds, ease: "easeOut" }}
            className="absolute inset-0"
          >
            {image ? (
              <button
                type="button"
                onClick={onOpenImage}
                aria-label={t("Show original slide")}
                className="block size-full cursor-zoom-in outline-none focus-visible:ring-1 focus-visible:ring-(--detail-fg) focus-visible:ring-inset"
              >
                <img
                  src={image.url}
                  alt={t("Slide {number}", { number: image.slideNumber })}
                  className="size-full object-contain"
                  draggable={false}
                />
              </button>
            ) : (
              <div className="flex size-full flex-col items-center justify-center gap-2 px-10 text-center">
                <p className="line-clamp-3 text-balance text-xl font-medium text-(--detail-fg)">
                  {titleCard.title}
                </p>
                {titleCard.presenter ? (
                  <p className="text-sm text-(--detail-fg)/70">
                    {titleCard.presenter}
                  </p>
                ) : null}
                {titleCard.role ? (
                  <p className="text-xs text-(--detail-fg)/70">
                    {titleCard.role}
                  </p>
                ) : null}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <div aria-hidden className="h-px w-full shrink-0 bg-(--detail-fg)/30" />

      <div
        className="grid min-h-0 flex-1 grid-cols-2 grid-rows-1"
        style={panel}
      >
        <nav
          ref={tocCellRef}
          aria-label={t("Table of contents")}
          className={CELL_SCROLL_CLASS}
        >
          {toc ? (
            <ModuleTOC
              data={toc}
              activeSlideNumber={slideNumber}
              onEntrySelect={onJumpToSlide}
              availableSlideNumbers={availableSlideNumbers}
              tone="inherit"
            />
          ) : null}
        </nav>
        <div className={CELL_SCROLL_CLASS}>
          {detailText ? (
            <div className="flex flex-col gap-2">
              {slideLabel ? (
                <p className="text-xs tabular-nums opacity-70">
                  {slideLabel}
                </p>
              ) : null}
              <SlideDetailsText markdown={detailText} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
